import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ExternalLink, PackageCheck } from 'lucide-react';
import { CONDITION_HINTS, isColorOption, swatchFor, type ProductCard, type ProductDetail } from '@shop/shared';
import { api, ApiError, assetUrl } from '../lib/api';
import { Link, useFormat, useLocale, useT } from '../lib/i18n';
import { useMoney, useSettings } from '../lib/settings';
import { useSeo } from '../lib/seo';
import { Markdown } from '../lib/markdown';
import { usePages } from '../lib/pages';
import { ProductGrid } from '../components/ProductCard';
import { Breadcrumbs, Button, ButtonLink, ErrorState, Img, Price, Reveal, SectionHeading, Skeleton, Stamp } from '../components/ui';
import { NotFoundPage } from './NotFound';

type Detail = ProductDetail & { related: ProductCard[] };

export function ProductPage() {
  const { slug = '' } = useParams();
  const locale = useLocale();
  const t = useT();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['product', locale, slug],
    queryFn: () => api<Detail>(`/api/store/products/${encodeURIComponent(slug)}`),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  if (error instanceof ApiError && error.status === 404) return <NotFoundPage title={t.product.notAvailable} />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (isLoading || !data) return <ProductSkeleton />;
  return <ProductView key={data.id} product={data} />;
}

function ProductSkeleton() {
  return (
    <div className="container-page grid gap-10 pt-8 pb-24 lg:grid-cols-12 lg:gap-16">
      <div className="photo lg:col-span-7">
        <Skeleton className="aspect-[4/5] w-full" />
      </div>
      <div className="space-y-5 lg:col-span-5 lg:pt-10">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-12 w-3/4" />
        <Skeleton className="h-6 w-24" />
        <Skeleton className="mt-10 h-24 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    </div>
  );
}

function ProductView({ product }: { product: Detail }) {
  const { store } = useSettings();
  const t = useT();
  const f = useFormat();
  const locale = useLocale();
  const money = useMoney();
  const { has: hasPage } = usePages();

  // Pre-select the first available variant.
  const initial = product.variants.find((v) => v.available) ?? product.variants[0];
  const [selected, setSelected] = useState<Record<string, string>>(initial?.options ?? {});
  const variant = product.variants.find((v) => product.options.every((o) => v.options[o.name] === selected[o.name])) ?? (product.options.length === 0 ? product.variants[0] : undefined);
  const label = (option: string) => product.optionLabels[option]?.name ?? option;
  const valueLabel = (option: string, value: string) => product.optionLabels[option]?.values[value] ?? value;

  // Gallery: jump to the variant's image when the selection changes.
  const [activeImage, setActiveImage] = useState(0);
  useEffect(() => {
    if (!variant?.imageMediaId) return;
    const idx = product.images.findIndex((i) => i.mediaId === variant.imageMediaId);
    if (idx >= 0) setActiveImage(idx);
  }, [variant?.imageMediaId, product.images]);

  const buyRef = useRef<HTMLDivElement>(null);
  const [showSticky, setShowSticky] = useState(false);
  useEffect(() => {
    const el = buyRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setShowSticky(!e.isIntersecting && e.boundingClientRect.top < 0), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const category = product.categories[0];
  const paragraphs = product.description.split(/\n{2,}/);
  const intro = paragraphs[0] ?? '';
  const rest = paragraphs.slice(1).join('\n\n');
  const onlyOne = Boolean(variant?.available && variant.lowStock && variant.stock === 1);

  useSeo({
    title: product.seoTitle || product.title,
    description: product.seoDescription || intro.slice(0, 300),
    image: product.images[0]?.url,
    type: 'product',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.title,
      description: intro,
      inLanguage: locale,
      image: product.images.map((i) => new URL(assetUrl(i.url), window.location.origin).toString()),
      sku: variant?.sku || undefined,
      brand: { '@type': 'Brand', name: store.name },
      offers: product.variants.map((v) => ({
        '@type': 'Offer',
        price: ((v.salePrice ?? v.price) / 100).toFixed(2),
        priceCurrency: store.currency,
        itemCondition: 'https://schema.org/UsedCondition',
        availability: v.available ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
        sku: v.sku || undefined,
      })),
    },
  });

  const isAvailable = (optionName: string, value: string) => {
    const probe = { ...selected, [optionName]: value };
    return product.variants.some((v) => v.available && Object.entries(probe).every(([k, val]) => v.options[k] === val));
  };

  // Items are bought on Vinted; one without a listing yet can't be bought.
  const vintedUrl = variant?.available ? product.vintedUrl : null;
  const buyLabel = !variant ? t.product.select : !variant.available ? t.product.soldButton : t.product.notOnVinted;
  const stockLine = !variant ? t.product.select : !variant.available ? t.product.sold : onlyOne ? t.product.onlyOne : variant.lowStock ? t.product.onlyLeft(variant.stock ?? 1) : t.product.inStock;
  const unitPrice = variant ? (variant.salePrice ?? variant.price) : product.price;

  const facts: { label: string; value: ReactNode }[] = [
    ...(product.decade ? [{ label: t.product.decade, value: f.decade(product.decade) }] : []),
    ...(product.condition
      ? [
          {
            label: t.product.condition,
            value: (
              <>
                <span className="font-semibold">{f.condition(product.condition)}</span>
                <span className="mt-0.5 block text-[15px] text-stone italic">{CONDITION_HINTS[locale][product.condition]}</span>
              </>
            ),
          },
        ]
      : []),
    ...(product.attributes.origin ? [{ label: t.product.origin, value: product.attributes.origin }] : []),
    ...(product.attributes.material ? [{ label: t.product.material, value: product.attributes.material }] : []),
    ...(product.attributes.dimensions ? [{ label: t.product.dimensions, value: product.attributes.dimensions }] : []),
    ...(product.attributes.conditionNote ? [{ label: t.product.notes, value: product.attributes.conditionNote }] : []),
  ];

  return (
    <div className="pb-24">
      <div className="container-page pt-6 pb-6 md:pt-8">
        <Breadcrumbs
          items={[
            { label: t.common.home, to: '/' },
            ...(category ? [{ label: category.name, to: `/categories/${category.slug}` }] : [{ label: t.common.shopAll, to: '/' }]),
            { label: product.title },
          ]}
        />
      </div>

      <div className="container-page grid gap-10 lg:grid-cols-12 lg:gap-16 xl:gap-20">
        <div className="lg:col-span-7">
          <Gallery images={product.images} active={activeImage} onChange={setActiveImage} title={product.title} sold={!product.inStock} />
        </div>

        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-[112px]">
            <div className="flex flex-wrap items-center gap-3">
              {category && (
                <Link to={`/categories/${category.slug}`} className="eyebrow link-underline">
                  {category.name}
                </Link>
              )}
              {product.inStock && product.oneOfAKind && (
                <Stamp tone="ink" className="-rotate-2">
                  {t.card.oneOfAKind}
                </Stamp>
              )}
            </div>
            <h1 className="font-display mt-4 text-[2.5rem] leading-[1.02] font-semibold text-balance md:text-[3.2rem]">{product.title}</h1>
            <div className="mt-5 flex items-end gap-3">
              <span className="price-tag rotate-[-2deg] py-2 pr-4 pl-8 text-[1.35rem]">
                {variant ? <Price price={variant.price} salePrice={variant.salePrice} /> : <Price price={product.price} salePrice={product.salePrice} priceMax={product.priceMax} from />}
              </span>
              <span className="typewriter pb-1 text-[12px] text-stone">{t.product.vat}</span>
            </div>
            {intro && <p className="mt-7 max-w-lg text-[18px] leading-[1.7] text-ink/85">{intro}</p>}

            <div className="mt-8 space-y-7">
              {product.options.map((opt) => {
                const color = isColorOption(opt.name);
                return (
                  <fieldset key={opt.name}>
                    <legend className="typewriter mb-3 flex w-full items-center justify-between text-[13px] tracking-[0.04em] uppercase">
                      <span>
                        <span className="text-stone">{label(opt.name)}:</span> <span className="font-bold">{selected[opt.name] ? valueLabel(opt.name, selected[opt.name]) : '—'}</span>
                      </span>
                    </legend>
                    <div className="flex flex-wrap gap-2.5">
                      {opt.values.map((value) => {
                        const active = selected[opt.name] === value;
                        const available = isAvailable(opt.name, value);
                        const swatch = color ? swatchFor(value) : null;
                        const text = valueLabel(opt.name, value);
                        if (swatch) {
                          return (
                            <button
                              key={value}
                              type="button"
                              onClick={() => setSelected((s) => ({ ...s, [opt.name]: value }))}
                              aria-pressed={active}
                              aria-label={`${text}${available ? '' : ` (${t.card.sold})`}`}
                              title={text}
                              className={`relative h-10 w-10 rounded-full p-[3px] transition ${active ? 'ring-[1.5px] ring-ink' : 'ring-1 ring-ink/25 hover:ring-ink/60'}`}
                            >
                              <span className="block h-full w-full rounded-full ring-1 ring-ink/15 ring-inset" style={{ background: swatch }} />
                              {!available && <span className="absolute inset-0 m-auto h-px w-[70%] rotate-45 bg-ink/70" />}
                            </button>
                          );
                        }
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setSelected((s) => ({ ...s, [opt.name]: value }))}
                            aria-pressed={active}
                            className={`typewriter min-w-[64px] rounded-[2px] border-[1.5px] px-4 py-2.5 text-[14px] transition ${active ? 'border-ink bg-ink text-paper' : 'border-ink/30 bg-paper hover:border-ink'} ${available ? '' : 'text-ash line-through decoration-ash'}`}
                          >
                            {text}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>
                );
              })}
            </div>

            <div ref={buyRef} className="mt-8">
              <p className={`typewriter mb-4 flex items-center gap-2 text-[13.5px] ${variant?.available ? (variant.lowStock ? 'text-sale' : 'text-ok') : 'text-stone'}`}>
                <span className={`h-2 w-2 rounded-full ${variant?.available ? (variant.lowStock ? 'bg-sale' : 'bg-ok') : 'bg-ash'}`} />
                {stockLine}
              </p>
              {vintedUrl ? (
                <ButtonLink to={vintedUrl} size="lg" variant="accent" className="w-full">
                  {t.product.buyOnVinted(money(unitPrice))} <ExternalLink className="h-4 w-4" />
                </ButtonLink>
              ) : (
                <Button size="lg" variant="accent" className="w-full" disabled>
                  {buyLabel}
                </Button>
              )}
            </div>

            <ul className="mt-8 space-y-3 border-t border-dashed border-ink/25 pt-7 text-[15.5px] text-ink/80">
              <li className="flex items-center gap-3">
                <PackageCheck className="h-4 w-4 text-accent" strokeWidth={1.6} /> {t.product.checked}
              </li>
            </ul>

            {facts.length > 0 && (
              <section className="mt-9 bg-paper p-5 shadow-[0_1px_2px_rgb(60_40_20_/_0.15)] sm:p-6" aria-labelledby="about-item">
                <h2 id="about-item" className="font-display border-b-[3px] border-double border-ink/50 pb-3 text-[1.35rem] font-semibold">
                  {t.product.about}
                </h2>
                <dl className="mt-1">
                  {facts.map((fact) => (
                    <div key={fact.label} className="grid grid-cols-[minmax(0,8.5rem)_1fr] gap-4 border-b border-dashed border-ink/20 py-3 last:border-b-0">
                      <dt className="typewriter pt-0.5 text-[12px] tracking-[0.1em] text-stone uppercase">{fact.label}</dt>
                      <dd className="text-[16.5px] leading-snug">{fact.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            <div className="mt-8 border-t border-ink/20">
              {rest && (
                <Accordion title={t.product.details} defaultOpen>
                  <Markdown source={rest} />
                </Accordion>
              )}
              <Accordion title={t.product.care}>
                <p className="text-[17px] leading-relaxed text-ink/80">
                  {t.product.careBody}
                  {hasPage('care') && (
                    <>
                      {' '}
                      {t.product.careMore}{' '}
                      <Link to="/pages/care" className="link-underline-static text-accent">
                        {t.product.careLink}
                      </Link>
                      .
                    </>
                  )}
                </p>
              </Accordion>
              <Accordion title={t.product.buying}>
                <p className="text-[17px] leading-relaxed text-ink/80">
                  {t.product.buyingBody}
                  {hasPage('how-to-buy') && (
                    <>
                      {' '}
                      <Link to="/pages/how-to-buy" className="link-underline-static text-accent">
                        {t.product.more}
                      </Link>
                    </>
                  )}
                </p>
              </Accordion>
            </div>
          </div>
        </div>
      </div>

      {product.related.length > 0 && (
        <section className="container-page pt-24 md:pt-32">
          <Reveal>
            <SectionHeading center eyebrow={t.product.relatedEyebrow} title={t.product.related} />
          </Reveal>
          <ProductGrid products={product.related} count={4} />
        </section>
      )}

      {/* Mobile sticky buy button */}
      <div
        className={`fixed inset-x-0 bottom-0 z-30 border-t border-ink/20 bg-bone/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur transition-transform duration-500 lg:hidden ${showSticky ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-display truncate text-[1.05rem] font-semibold">{product.title}</p>
            <p className="typewriter truncate text-[12px] text-stone">{variant && variant.title !== 'Default' ? variant.title : stockLine}</p>
          </div>
          {vintedUrl ? (
            <ButtonLink to={vintedUrl} variant="accent">
              {t.product.buyShort(money(unitPrice))}
            </ButtonLink>
          ) : (
            <Button variant="accent" disabled>
              {buyLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Accordion({ title, children, defaultOpen }: { title: string; children: ReactNode; defaultOpen?: boolean }) {
  return (
    <details open={defaultOpen} className="group border-b border-ink/20">
      <summary className="typewriter flex cursor-pointer list-none items-center justify-between py-5 text-[12.5px] font-bold tracking-[0.14em] uppercase [&::-webkit-details-marker]:hidden">
        {title}
        <ChevronDown className="h-4 w-4 transition-transform duration-300 group-open:rotate-180" strokeWidth={1.6} />
      </summary>
      <div className="pb-6">{children}</div>
    </details>
  );
}

function Gallery({ images, active, onChange, title, sold }: { images: ProductDetail['images']; active: number; onChange: (i: number) => void; title: string; sold: boolean }) {
  const t = useT();
  const locale = useLocale();
  const scroller = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<{ x: number; y: number } | null>(null);
  const safeActive = Math.min(active, Math.max(0, images.length - 1));
  const altFor = (img: ProductDetail['images'][number]) => (locale === 'en' && img.alt) || title;

  // Keep the mobile carousel in sync with the active image.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const target = el.children[safeActive] as HTMLElement | undefined;
    if (target && Math.abs(el.scrollLeft - target.offsetLeft) > 4) el.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }, [safeActive]);

  const onScroll = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const el = scroller.current;
        if (!el) return;
        const idx = Math.round(el.scrollLeft / el.clientWidth);
        if (idx !== safeActive) onChange(idx);
      }, 80);
    };
  }, [onChange, safeActive]);

  if (images.length === 0) {
    return (
      <div className="photo">
        <div className="aspect-[4/5] bg-sand" />
      </div>
    );
  }
  const current = images[safeActive];
  const soldStamp = sold && (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
      <Stamp className="-rotate-12 bg-paper/70 px-6 py-3 text-[28px]!">{t.card.sold}</Stamp>
    </div>
  );

  return (
    <div className="lg:flex lg:gap-6">
      {/* Thumbnails (desktop) */}
      {images.length > 1 && (
        <div className="hidden w-[88px] shrink-0 flex-col gap-4 lg:flex">
          {images.map((img, i) => (
            <button
              key={img.id}
              onClick={() => onChange(i)}
              className={`photo p-1! transition ${i === safeActive ? 'ring-[1.5px] ring-ink' : 'opacity-70 hover:opacity-100'}`}
              aria-label={t.product.showImage(i + 1)}
              aria-current={i === safeActive}
            >
              <div className="aspect-[4/5] overflow-hidden bg-sand">
                <Img src={img.thumbUrl} alt="" className="h-full w-full object-cover" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Main image (desktop) with hover zoom */}
      <div className={`photo hidden flex-1 lg:block ${sold ? 'sold-photo' : ''}`}>
        <div
          className="photo-inner grain aspect-[4/5] cursor-zoom-in"
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setZoom({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
          }}
          onMouseLeave={() => setZoom(null)}
        >
          <Img
            key={current.id}
            src={current.url}
            thumb={current.thumbUrl}
            alt={altFor(current)}
            priority
            sizes="(min-width: 1024px) 55vw, 100vw"
            className="h-full w-full object-cover"
            style={zoom ? { transform: 'scale(1.7)', transformOrigin: `${zoom.x}% ${zoom.y}%`, transition: 'transform 0.2s ease-out' } : { transition: 'transform 0.4s ease-out' }}
          />
          {soldStamp}
        </div>
      </div>

      {/* Swipeable carousel (mobile) */}
      <div className={`photo relative lg:hidden ${sold ? 'sold-photo' : ''}`}>
        <div className="relative overflow-hidden">
          <div ref={scroller} onScroll={onScroll} className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto">
            {images.map((img, i) => (
              <div key={img.id} className="aspect-[4/5] w-full shrink-0 snap-center bg-sand">
                <Img src={img.url} thumb={img.thumbUrl} alt={altFor(img)} priority={i === 0} sizes="100vw" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
          {soldStamp}
          {images.length > 1 && (
            <div className="absolute inset-x-0 bottom-4 flex justify-center gap-1.5">
              {images.map((img, i) => (
                <button key={img.id} onClick={() => onChange(i)} className={`h-1.5 rounded-full transition-all ${i === safeActive ? 'w-5 bg-ink' : 'w-1.5 bg-ink/30'}`} aria-label={t.product.image(i + 1)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
